import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import ExploreIcon from "@mui/icons-material/Explore";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton } from "@mui/material";

// Define the PlacesAutocomplete props interface
interface PlacesAutocompleteProps {
  setCoordinates?: Dispatch<
    SetStateAction<{ lat: number | null; lng: number | null }>
  >;
  defaultValue?: string;
  onSelect?: (address: string, place: google.maps.places.PlaceResult) => void;
  setPlaceId?: (placeId: string) => void;
  inputCleared?: () => void;
  placeholder?: string;
  types?: string[];
  className?: string;
}

// Main component that wraps everything with APIProvider
const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = (
  props
) => {
  return (
    <APIProvider
      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
      libraries={['places']}
      language="el" // or "en"
      region="gr"
    >
      <InnerPlacesAutocomplete {...props} />
    </APIProvider>
  );
};

// Inner component that uses the Maps library
const InnerPlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  defaultValue = "",
  onSelect,
  setPlaceId,
  inputCleared,
  setCoordinates,
  types = [
    "locality",
    "administrative_area_level_3",
    "administrative_area_level_2",
  ],
  placeholder = "Αναζήτησε πάρτυ με βάση την τοποθεσία",
  className,
}) => {
  const [address, setAddress] = useState(defaultValue || "");
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const places = useMapsLibrary("places");

  // Colors for playful UI
  const colors = {
    primary: "#6A5ACD", // Soft purple
    secondary: "#FF9671", // Coral
    highlight: "#FFC75F", // Soft yellow
    lightBg: "#FFF9F0", // Off-white
    border: "#FFE3B8", // Light orange
  };

  // Generate a random balloon color for icon
  const balloonColors = ["#FF6F91", "#FF9671", "#FFC75F", "#845EC2", "#00C9A7"];
  const randomBalloonColor =
    balloonColors[Math.floor(Math.random() * balloonColors.length)];

  // Initialize Autocomplete
  useEffect(() => {
    if (!places || !inputRef.current) return;

    // Basic options
    const options = {
      fields: ["address_components", "geometry", "name", "formatted_address", "place_id"],
      types,
      componentRestrictions: { country: "gr" }
    };

    // Create the autocomplete instance
    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  // Set up place_changed listener
  useEffect(() => {
    if (!placeAutocomplete) return;

    // Add place_changed event listener
    placeAutocomplete.addListener("place_changed", () => {
      const place = placeAutocomplete.getPlace();

      if (place && place.geometry && place.geometry.location) {
        // Get coordinates
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Update address
        setAddress(place.formatted_address || place.name || "");

        // Set coordinates
        if (setCoordinates) {
          setCoordinates({ lat, lng });
        }

        // Set place ID if needed
        if (setPlaceId && place.place_id) {
          setPlaceId(place.place_id);
        }

        // Call onSelect callback
        if (onSelect) {
          onSelect(place.formatted_address || "", place);
        }
      }
    });

    // Cleanup listener on unmount
    return () => {
      if (placeAutocomplete) {
        google.maps.event.clearInstanceListeners(placeAutocomplete);
      }
    };
  }, [onSelect, placeAutocomplete, setCoordinates, setPlaceId]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);

    if (!e.target.value) {
      if (setCoordinates) {
        setCoordinates({ lat: null, lng: null });
      }
      if (setPlaceId) {
        setPlaceId("");
      }
    }
  };

  // Handle clear button click
  const handleClear = () => {
    setAddress("");
    if (setCoordinates) {
      setCoordinates({ lat: null, lng: null });
    }
    if (setPlaceId) {
      setPlaceId("");
    }
    if (inputCleared) {
      inputCleared();
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div className="w-full relative">
      <div
        className={`${className ? className : `flex items-center border-2 rounded-full ${
          isFocused ? `border-${colors.primary} shadow-lg` : `border-${colors.border}`
        }`} bg-white transition-all duration-300`}
        style={{
          boxShadow: !className && isFocused ? `0 0 0 4px rgba(106, 90, 205, 0.1)` : "none",
          transform: !className && isFocused ? "scale(1.01)" : "scale(1)",
        }}
      >
        <div
          style={{
            color: randomBalloonColor,
            animation: isFocused ? "bounce 1.5s infinite" : "none",
          }}
          className="ml-3 mr-1"
        >
          <ExploreIcon style={{ fontSize: 24 }} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={handleInputChange}
          placeholder={placeholder}
          style={{
            width: "calc(100% - 65px)",
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
          }}
          className="py-3 px-2 w-full text-sm shadow-none focus:[box-shadow:none] focus:shadow-none border-none outline-none focus:outline-none"
        />
        {address && (
          <IconButton
            onClick={handleClear}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9CA3AF",
              padding: "4px",
              marginRight: "8px",
            }}
          >
            <ClearIcon style={{ fontSize: 20 }} />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default PlacesAutocomplete;
